// src/cockpit4/components/KnowledgeMonetization.tsx
// ============================================
// 知识商业化（知识中心 P2-功能10）- v2.0 详细改进计划
// 知识库产品化 / 知识付费 / 知识产权管理 / 外部知识合作
// 数据来源：localStorage（xeaos_knowledge_monetization）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BadgeDollarSign, BookOpen, ShieldCheck, Handshake, TrendingUp } from "lucide-react";

interface KnowledgeProduct {
  id: string;
  name: string;
  type: "课程" | "报告" | "咨询服务";
  price: number;
  sold: number;
  revenue: number;
  rating: number;
  status: "上架" | "草稿";
}

const INITIAL_PRODUCTS: KnowledgeProduct[] = [
  { id: "k1", name: "企业 AI 落地实战课", type: "课程", price: 299, sold: 42, revenue: 12558, rating: 4.8, status: "上架" },
  { id: "k2", name: "2026 企业数字化白皮书", type: "报告", price: 99, sold: 86, revenue: 8514, rating: 4.6, status: "上架" },
  { id: "k3", name: "AI 经营团队搭建咨询", type: "咨询服务", price: 8000, sold: 3, revenue: 24000, rating: 5.0, status: "上架" },
  { id: "k4", name: "私域运营实操手册", type: "课程", price: 199, sold: 0, revenue: 0, rating: 0, status: "草稿" },
];

const KEY = "xeaos_knowledge_monetization";

function load(): KnowledgeProduct[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as KnowledgeProduct[];
  } catch { /* ignore */ }
  return INITIAL_PRODUCTS;
}

export default function KnowledgeMonetization() {
  const [products, setProducts] = useState<KnowledgeProduct[]>(load);

  const stats = useMemo(() => {
    const totalRevenue = products.reduce((a, p) => a + p.revenue, 0);
    const totalSold = products.reduce((a, p) => a + p.sold, 0);
    const onSale = products.filter((p) => p.status === "上架").length;
    const avgRating = products.filter((p) => p.rating > 0).reduce((a, p) => a + p.rating, 0) / Math.max(1, products.filter((p) => p.rating > 0).length);
    return { totalRevenue, totalSold, onSale, avgRating };
  }, [products]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BadgeDollarSign size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">知识商业化</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Knowledge Monetization</span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="知识产品" value={`${products.length} 个`} color="#94A3B8" />
        <MetricCard label="总销售额" value={`¥${stats.totalRevenue.toLocaleString()}`} color="#34D399" />
        <MetricCard label="已售" value={`${stats.totalSold} 份`} color="#38BDF8" />
        <MetricCard label="平均评分" value={stats.avgRating ? `${stats.avgRating.toFixed(1)} / 5` : "-"} color="#FBBF24" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">知识产品库</span>
        </div>
        <div className="space-y-1.5">
            {products.map((p) => (
              <div key={p.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{p.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{p.type}</span>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${p.status === "上架" ? "bg-sky-500/15 text-sky-400" : "bg-gray-500/15 text-gray-500"}`}>{p.status}</span>
                  <span className="ml-auto text-[10px] font-mono font-semibold text-emerald-400">¥{p.price.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                  <span>已售 {p.sold} 份</span>
                  <span className="text-emerald-400">¥{p.revenue.toLocaleString()}</span>
                  {p.rating > 0 && <span className="flex items-center gap-0.5 text-amber-400">★ {p.rating}</span>}
                  {p.status === "草稿" && <button className="ml-auto px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">上架</button>}
                </div>
              </div>
            ))}
          </div>
        </div>

      <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-teal-300 mb-1"><ShieldCheck size={10} /> 知识产权管理</div>
          <div className="text-gray-400">26 篇内容已登记原创 · 3 项商标保护中 · 知识授权模板已就绪</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-sky-300 mb-1"><Handshake size={10} /> 外部知识合作</div>
          <div className="text-gray-400">2 个行业媒体已达成内容互推 · 1 个知识平台合作洽谈中</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-amber-300 mb-1"><TrendingUp size={10} /> 变现趋势</div>
          <div className="text-gray-400">本月知识变现环比 +42% · 咨询业务占比 58%</div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

